import {
  type ShippingItem,
  type ShippingItemsMeasures,
} from '../shipping-items/domain/ShippingItem'
import { isPast } from '../utils/date'
import { isNumber, round } from '../utils/number'
import { type BaseCarrier } from './Carrier'
import { hasCommodity, hasSize, hasWeight, isItemEmpty, type CommodityItem } from './CommodityItem'
import { type Contract } from './Contract'
import { type LoadMeasures } from './LoadMeasures'
import { type ManagedBy } from './ManagedBy'
import { type Mode } from './Mode'
import { type Shipper } from './Shipper'
import { hasCoordinates, type Stop } from './Stop'
import { type TruckType } from './Truck'

export type TenderEquipmentRequirements = {
  temperature?: string
  temperatureUnit?: string
}

export type Load = {
  carrier: BaseCarrier
  contract?: Contract
  createdAt?: Date
  deadline?: Date
  equipmentRequirements?: TenderEquipmentRequirements
  equipmentSubtypes?: Array<EquipmentSubtypes>
  equipmentType: TruckType
  flatRate: string
  id: string
  items: Array<CommodityItem>
  shippingItems: Array<ShippingItem>
  shippingMeasures: ShippingItemsMeasures
  measures?: LoadMeasures
  mileage: number
  mode: Mode
  notes?: string
  shareDetails?: boolean
  shipper: Shipper
  stops: Array<Stop>
  managedBy?: ManagedBy
}

export type EquipmentSubtypes =
  | 'regular_flatbed'
  | 'conestoga'
  | 'step_deck'
  | 'hotshot'
  | 'double_drop'
  | 'gooseneck'
  | 'low_boy'
  | 'maxi'
  | 'power_only'
  | 'removable_gooseneck'
  | 'conestoga_step_deck'

export function getContact(load: Load): {
  name: string
  email: string
  phone?: string
} {
  if (load.managedBy) {
    return {
      name: load.managedBy.contactName,
      email: load.managedBy.contactEmail,
      phone: load.managedBy.contactPhone,
    }
  }

  return {
    name: load.shipper.companyName,
    email: load.shipper.email,
    phone: load.shipper.phoneNumber,
  }
}

export function getListOfCommodities({ items }: Load): Array<string> {
  return items.filter(hasCommodity).map(({ commodity }) => commodity!)
}

export function getRatePerMile({ flatRate, mileage }: Load) {
  return round(Number(flatRate) / mileage, 2)
}

export function getTotalWeight(load: Load): number {
  return load.items
    .map(({ weight }) => weight)
    .filter(isNumber)
    .reduce((acc, weight) => acc + weight, 0)
}

export function hasItems({ items }: Load): boolean {
  return items.length > 0 && !items.every(isItemEmpty)
}

/**
 * Determine if at least one item has the commodity field with information
 */
export function hasItemCommodity({ items }: Load): boolean {
  return items.some(hasCommodity)
}

/**
 * Determine if at least one item has one of the size fields with information
 */
export function hasItemSize({ items }: Load): boolean {
  return items.some(hasSize)
}

/**
 * Determine if at least one item has the weight field with information
 */
export function hasItemWeight({ items }: Load): boolean {
  return items.some(hasWeight)
}

export function hasMileage({ mileage }: Load): boolean {
  return isNumber(mileage) && mileage > 0
}

export function hasNotes({ notes }: Load) {
  return notes !== undefined
}

export function hasRfpContract({ contract }: Load) {
  return contract?.rfp?.inviteUUID !== undefined
}

export function isOfferExpired({ deadline }: Load) {
  if (deadline) return isPast(deadline)
  return false
}

export function hasPuDelCoordinates({ stops }: Load) {
  if (stops.length < 2) return false

  const [puStop] = stops
  const delStop = stops[stops.length - 1]

  return hasCoordinates(puStop) && hasCoordinates(delStop)
}

export function hasEquipmentSubtypes({ equipmentSubtypes }: Load): boolean {
  return Array.isArray(equipmentSubtypes) && equipmentSubtypes.length > 0
}
